import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Calendar,
  Clock,
  Video,
  Download,
  Info,
  Users,
  MessageSquare
} from 'lucide-react'

export default function CEOFeedbackModal({ open, onClose }) {
  // Session details
  const sessionInfo = {
    title: 'Demo Preparation with India FinTech Forum\'s CEO',
    subtitle: 'Session on Demo Preparation with India FinTech Forum\'s CEO',
    date: '2025-10-17', // October 17, 2025
    startTime: '16:00', // 4:00 PM IST
    endTime: '17:00', // 5:00 PM IST
    timezone: 'Asia/Kolkata',
    zoomLink: 'https://us02web.zoom.us/j/81924110812?pwd=WaaOWDOYU6WKFLF96taAGUM0uWUDRs.1',
    meetingId: '819 2411 0812',
    passcode: '194496',
    location: 'Zoom Meeting',
    description: 'Get valuable feedback on your pitch deck and presentation from India FinTech Forum\'s CEO before the main event.'
  }

  const generateGoogleCalendarUrl = () => {
    const startDate = new Date(`${sessionInfo.date}T${sessionInfo.startTime}:00`)
    const endDate = new Date(`${sessionInfo.date}T${sessionInfo.endTime}:00`)

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
    }

    const details = `Demo Preparation with India FinTech Forum's CEO.

Join Zoom Meeting:
${sessionInfo.zoomLink}

Meeting ID: ${sessionInfo.meetingId}
Passcode: ${sessionInfo.passcode}

Learn more about IFTA here: https://bit.ly/ifta_2025`

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: sessionInfo.title,
      details: details,
      location: sessionInfo.zoomLink,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`
    })

    return `https://calendar.google.com/calendar/render?${params.toString()}`
  }

  const generateOutlookUrl = () => {
    const startDate = new Date(`${sessionInfo.date}T${sessionInfo.startTime}:00`)
    const endDate = new Date(`${sessionInfo.date}T${sessionInfo.endTime}:00`)

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      startdt: startDate.toISOString(),
      enddt: endDate.toISOString(),
      subject: sessionInfo.title,
      body: `Join the CEO Feedback Session for IFTA 2025 Demo Companies.\n\nGet valuable feedback on your pitch deck and presentation.\n\nJoin Zoom Meeting:\n${sessionInfo.zoomLink}\n\nMeeting ID: ${sessionInfo.meetingId}\nPasscode: ${sessionInfo.passcode}\n\nLearn more about IFTA here: https://bit.ly/ifta_2025`,
      location: sessionInfo.zoomLink
    })

    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
  }

  const downloadICS = () => {
    const startDate = new Date(`${sessionInfo.date}T${sessionInfo.startTime}:00`)
    const endDate = new Date(`${sessionInfo.date}T${sessionInfo.endTime}:00`)

    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
    }

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//IFTA//CEO Feedback Session//EN
BEGIN:VEVENT
UID:${Date.now()}@ifta.com
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:${sessionInfo.title}
DESCRIPTION:Join Zoom Meeting:\\n${sessionInfo.zoomLink}\\nMeeting ID: ${sessionInfo.meetingId}\\nPasscode: ${sessionInfo.passcode}\\n\\nLearn more about IFTA here: https://bit.ly/ifta_2025
LOCATION:${sessionInfo.zoomLink}
STATUS:CONFIRMED
SEQUENCE:0
END:VEVENT
END:VCALENDAR`

    const blob = new Blob([icsContent], { type: 'text/calendar' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'ceo-feedback-session-ifta-2025.ics'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:${minutes} ${ampm} IST`
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] !flex !flex-col !overflow-hidden !p-0 [&>button]:top-6 [&>button]:right-6">
        <DialogHeader className="flex-shrink-0 p-6 pr-16 border-b">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <Video className="h-6 w-6 text-purple-600" />
              </div>
              <DialogTitle className="text-lg flex-1 min-w-0">
                Demo Preparation with India FinTech Forum's CEO
              </DialogTitle>
            </div>
            <Badge variant="outline" className="whitespace-nowrap flex-shrink-0 font-normal text-xs">
              Oct 17
            </Badge>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Session Description */}
          {/* <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">About This Session</h4>
                <p className="text-sm text-gray-600 mb-3">
                  {sessionInfo.description}
                </p>
                <p className="text-sm text-gray-600">
                  Schedule and participate in this feedback session with CEO. This is a valuable
                  opportunity to get expert feedback on your pitch deck, presentation style, and
                  demo strategy before the main event.
                </p>
              </div>
            </div>
          </div> */}

          {/* Session Details */}
          <div className="border border-gray-200 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold mb-4">Session Details</h3>

            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Date</p>
                  <p className="text-sm text-gray-600">{formatDate(sessionInfo.date)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Time</p>
                  <p className="text-sm text-gray-600">
                    {formatTime(sessionInfo.startTime)} - {formatTime(sessionInfo.endTime)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Video className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Online Meeting</p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <a
                      href={sessionInfo.zoomLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs break-all font-semibold"
                    >
                      Zoom Link
                    </a>
                    <p className="text-xs">Meeting ID: {sessionInfo.meetingId}</p>
                    <p className="text-xs">Passcode: {sessionInfo.passcode}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Section */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold">Add to Calendar</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Add this event to your calendar to receive reminders
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                onClick={() => window.open(generateGoogleCalendarUrl(), '_blank')}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Calendar className="h-4 w-4" />
                Google Calendar
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(generateOutlookUrl(), '_blank')}
                className="flex items-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                Outlook
              </Button>
              <Button
                variant="outline"
                onClick={downloadICS}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download .ics
              </Button>
            </div>
          </div>
          {/* What to Expect */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">What to Expect</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Direct Feedback</p>
                  <p className="text-sm text-gray-600">
                    Get personalized feedback on your pitch deck and presentation approach
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Q&A Session</p>
                  <p className="text-sm text-gray-600">
                    Ask questions about the demo day, judging criteria, and expectations
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-purple-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Best Practices</p>
                  <p className="text-sm text-gray-600">
                    Learn presentation tips and strategies from past successful demos
                  </p>
                </div>
              </div>
            </div>
          </div>


          {/* Important Notes */}
          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Important Notes</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>This session is highly recommended for all demo companies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>Prepare your pitch deck and key questions in advance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>Session will be recorded for those who cannot attend live</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>Maximum 2 participants per company</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>Learn more about IFTA at https://bit.ly/ifta_2025</span>
                  </li>
                </ul>
              </div>
            </div>
          </div> */}
        </div>
      </DialogContent>
    </Dialog>
  )
}